#!/usr/bin/env node

import { Command } from "commander"
import chalk from "chalk"
import { addCommand } from "./commands/add.js"
import { initCommand } from "./commands/init.js"
import { buildCommand } from "./commands/build.js"
import { scanCommand } from "./commands/scan.js"
import { listCommand } from "./commands/list.js"
import { diffCommand } from "./commands/diff.js"
import { cacheClearCommand } from "./commands/cache.js"
import { infoCommand } from "./commands/info.js"
import { registryCleanCommand } from "./commands/registry.js"
import { resetCommand } from "./commands/reset.js"
import { logger } from "./utils/logger.js"
import { getCliVersion } from "./utils/cli-version.js"
import { resolve } from "node:path"

const program = new Command()

program
  .option("-c, --cwd <dir>", "Working directory", process.cwd())
  .option("-v, --verbose", "Enable verbose output")
  .option("--no-cache", "Bypass registry cache")
  .name("ui8kit")
  .description("A CLI for adding UI components to your Vite React projects (UI8Kit registry)")
  .version(getCliVersion())

program
  .command("list")
  .description("List available components in registry")
  .option("-r, --registry <type>", "Registry type: ui", "ui")
  .option("--json", "Output raw JSON")
  .option("--registry-url <url>", "Override CDN registry base URL")
  .option("--registry-version <version>", "Override registry @latest version")
  .option("--strict-cdn", "Disable fallback CDN providers when override is provided")
  .action((options) => listCommand(options))

program
  .command("diff")
  .description("Show local vs registry differences")
  .argument("[component]", "Component name")
  .option("-r, --registry <type>", "Registry type: ui", "ui")
  .option("--json", "Output diff in machine-readable JSON")
  .option("--registry-url <url>", "Override CDN registry base URL")
  .option("--registry-version <version>", "Override registry @latest version")
  .option("--strict-cdn", "Disable fallback CDN providers when override is provided")
  .action((component, options) => diffCommand(component, options))

program
  .command("cache")
  .description("Manage local cache")
  .command("clear")
  .description("Clear registry cache")
  .action(cacheClearCommand)

program
  .command("registry")
  .description("Manage generated registry artifacts")
  .command("clean")
  .description("Remove generated registry build artifacts")
  .option("--dry-run", "Show what would be removed without deleting")
  .option("--all", "Also remove src/registry.json if it exists")
  .option("-f, --force", "Skip confirmation prompt")
  .action(registryCleanCommand)

program
  .command("reset")
  .description("Reset local UI8Kit state")
  .option("--dry-run", "Show what would be removed without deleting")
  .option("--with-cache", "Also clear CLI cache data")
  .option("-y, --yes", "Skip prompts and perform reset")
  .option("-f, --force", "Skip confirmation prompt")
  .action(resetCommand)

program
  .command("info")
  .description("Show environment and config diagnostics")
  .option("--json", "Output diagnostics as JSON")
  .option("--cdn", "Show resolved CDN order and active overrides")
  .action(infoCommand)

program
  .command("init")
  .description("Initialize UI8Kit structure in your project")
  .option("-y, --yes", "Skip prompts and use defaults")
  .option("-r, --registry <type>", "Registry type: ui", "ui")
  .option("--registry-url <url>", "Override CDN registry base URL")
  .option("--registry-version <version>", "Override registry @latest version")
  .option("--strict-cdn", "Disable fallback CDN providers when override is provided")
  .option("--import-style <alias|package>", "Rewrite imports as alias paths or @ui8kit/core package imports", "alias")
  .action(initCommand)

program
  .command("add")
  .description("Add components to your project from the registry")
  .argument("[components...]", "Components to add")
  .option("-a, --all", "Install all available components")
  .option("-f, --force", "Overwrite existing files")
  .option("-r, --registry <type>", "Registry type: ui", "ui")
  .option("--dry-run", "Show what would be installed without installing")
  .option("--retry", "Aggressive retry mode (3 attempts per CDN request)")
  .option("--registry-url <url>", "Override CDN registry base URL")
  .option("--registry-version <version>", "Override registry @latest version")
  .option("--strict-cdn", "Disable fallback CDN providers when override is provided")
  .action(addCommand)

program
  .command("scan")
  .description("Scan and generate registry from existing components")
  .option("-r, --registry <type|path>", "Registry type (ui) or custom path", "ui")
  .option("-o, --output <file>", "Output registry file")
  .option("-s, --source <dir>", "Source directory to scan")
  .action(async (options) => {
    await scanCommand(options)
  })

program
  .command("build")
  .description("Build components registry")
  .argument("[registry]", "Path to registry.json file", "./src/registry.json")
  .option("-o, --output <path>", "Output directory", "./packages/registry/r")
  .action(buildCommand)

program.on("command:*", () => {
  console.error(chalk.red(`Invalid command: ${program.args.join(" ")}`))
  console.log("See --help for a list of available commands.")
  process.exit(1)
})

program.hook("preAction", (_, actionCommand) => {
  const actionOptions = actionCommand?.opts?.() as {
    verbose?: boolean
    cwd?: string
  } | undefined
  const globalOptions = program.opts() as {
    verbose?: boolean
    cwd?: string
  }

  const verbose = globalOptions.verbose || actionOptions?.verbose
  const cwd = actionOptions?.cwd || globalOptions.cwd

  if (verbose) {
    logger.setVerbose(true)
  }

  if (cwd && resolve(process.cwd()) !== resolve(cwd)) {
    process.chdir(cwd)
  }
})

program.parse()